import CheckIcon from '@mui/icons-material/Check'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppContext } from '../../../providers/AppProvider/AppProvider'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useGetShopLogoForDymoQuery } from '../../../redux/api'
import { CenteredLoading } from '../../CenteredLoading/CenteredLoading'
import { Printer } from './GetPrinters'
import {
  getTestAddressLabelXml0,
  getTestAddressLabelXml1,
  getTestAddressLabelXml2
} from './labelfile'
import {
  getTestAddressNormalXml0,
  getTestAddressNormalXml1
} from './labelfileNormal'
import {
  getTestAddressShippingXml0,
  getTestAddressShippingXml1,
  getTestAddressShippingXml2
} from './labelfileShipping'

interface LabelVersion {
  size: string
  version: string
}

interface Props {
  printer: Printer
}

const PreviewAndPrint = ({ printer }: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { vehicle } = useContext(RepairOrderContext)
  const { shopProfile } = useContext(AppContext)

  const [previewLabelSrc, setPreviewLabelSrc] = useState('')
  const [printedLabelSrc, setPrintedLabelSrc] = useState('')
  const [labelVersion, setLabelVersion] = useState<LabelVersion>({
    size: '2x2.3125',
    version: 'middleNormal'
  })
  const [showLine1, setShowLine1] = useState(true)
  const [showLine2, setShowLine2] = useState(true)
  const [showLine3, setShowLine3] = useState(true)
  const [showLine4, setShowLine4] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const { data: shopLogoDymoData } = useGetShopLogoForDymoQuery({
    authorization: accessToken,
    shop: shopId
  })

  const numLinesNormal = useMemo(() => {
    let numLines = 0
    if (showLine1) {
      numLines = numLines + 1
    }
    if (showLine2) {
      numLines = numLines + 1
    }
    if (showLine3) {
      numLines = numLines + 2
    }
    if (showLine4) {
      numLines = numLines + 2
    }
    return numLines
  }, [showLine1, showLine2, showLine3, showLine4])

  const testAddressLabelXml = useMemo(() => {
    const name = shopProfile?.info?.name
    const phone = shopProfile?.info?.phone
    const nextOilServiceDate =
      vehicle?.info?.nextOilServiceDate?.toLocaleDateString()
    const nextOilServiceMileage = vehicle?.info?.nextOilServiceMileage
    const nextInspectionDate =
      vehicle?.info?.nextInspectionDate?.toLocaleDateString()

    const line1 = (name === undefined ? 'Service Label' : name) + '\n'
    const line2 = 'Phone: ' + (phone === undefined ? 'N/A' : phone) + '\n'
    let line3 = 'Next Oil Service: '
    let line3NewLine = 'Next Oil Service:\n'
    if (
      nextOilServiceDate === undefined &&
      nextOilServiceMileage === undefined
    ) {
      line3 = line3 + 'N/A'
      line3NewLine = line3NewLine + 'N/A'
    } else if (
      nextOilServiceDate !== undefined &&
      nextOilServiceMileage !== undefined
    ) {
      line3 =
        line3 + nextOilServiceDate + ' or ' + nextOilServiceMileage + ' miles'
      line3NewLine =
        line3NewLine +
        nextOilServiceDate +
        ' or ' +
        nextOilServiceMileage +
        ' miles'
    } else if (nextOilServiceDate !== undefined) {
      line3 = line3 + nextOilServiceDate
      line3NewLine = line3NewLine + nextOilServiceDate
    } else if (nextOilServiceMileage !== undefined) {
      line3 = line3 + nextOilServiceMileage + ' miles'
      line3NewLine = line3NewLine + nextOilServiceMileage + ' miles'
    }
    line3 = line3 + '\n'
    line3NewLine = line3NewLine + '\n'
    const line4 =
      'Next Inspection: ' +
      (nextInspectionDate === undefined ? 'N/A' : nextInspectionDate) +
      '\n'
    const line4NewLine =
      'Next Inspection:\n' +
      (nextInspectionDate === undefined ? 'N/A' : nextInspectionDate) +
      '\n'

    const lineToShow =
      (showLine1 ? line1 : '') +
      (showLine2 ? line2 : '') +
      (showLine3 ? line3 : '') +
      (showLine4 ? line4 : '')
    const lineToShowVertical =
      (showLine1 ? line1 : '') +
      (showLine2 ? line2 : '') +
      (showLine3 ? line3NewLine : '') +
      (showLine4 ? line4NewLine : '')
    if (labelVersion.version === 'smallAddress') {
      return getTestAddressLabelXml2(lineToShow, shopLogoDymoData || '')
    } else if (labelVersion.version === 'largeAddress') {
      return getTestAddressLabelXml1(lineToShow, shopLogoDymoData || '')
    } else if (labelVersion.version === 'middleShipping') {
      return getTestAddressShippingXml1(lineToShow, shopLogoDymoData || '')
    } else if (labelVersion.version === 'rightShipping') {
      return getTestAddressShippingXml2(lineToShow, shopLogoDymoData || '')
    } else if (labelVersion.version === 'middleNormal') {
      return getTestAddressNormalXml1(
        lineToShowVertical,
        shopLogoDymoData || '',
        numLinesNormal
      )
    } else {
      if (labelVersion.size === '1x3') {
        return getTestAddressLabelXml0(lineToShow)
      } else if (labelVersion.size === '2x4') {
        return getTestAddressShippingXml0(lineToShow)
      } else if (labelVersion.size === '2x2.3125') {
        return getTestAddressNormalXml0(lineToShowVertical)
      }
    }
  }, [
    shopProfile?.info?.name,
    shopProfile?.info?.phone,
    vehicle?.info?.nextInspectionDate,
    vehicle?.info?.nextOilServiceDate,
    vehicle?.info?.nextOilServiceMileage,
    showLine1,
    showLine2,
    showLine3,
    showLine4,
    shopLogoDymoData,
    labelVersion.size,
    labelVersion.version,
    numLinesNormal
  ])

  const generatePreview = useCallback(async () => {
    setLoading(true)
    try {
      const label =
        window.dymo.label.framework.openLabelXml(testAddressLabelXml)
      const pngData = await label.renderAsync()
      const labelSrc = 'data:image/png;base64,' + pngData
      setPreviewLabelSrc(labelSrc)
    } catch (error) {
      setErrorMessage(
        'Printer error. Please check that printer is connected and dymo software is installed properly.'
      )
    } finally {
      setLoading(false)
    }
  }, [testAddressLabelXml])

  useEffect(() => {
    generatePreview()
  }, [generatePreview])

  const printLabel = useCallback(async () => {
    setLoading(true)
    try {
      const label =
        window.dymo.label.framework.openLabelXml(testAddressLabelXml)
      const pngData = await label.renderAsync()
      const labelSrc = 'data:image/png;base64,' + pngData
      setPrintedLabelSrc(labelSrc)
      label.print(printer.name)
    } catch (error) {
      setErrorMessage(
        'Printer error. Please check that printer is connected and dymo software is installed properly.'
      )
    } finally {
      setLoading(false)
    }
  }, [printer.name, testAddressLabelXml])

  return (
    <Box sx={{ width: '100%', minHeight: 250 }}>
      {errorMessage !== '' && <Alert severity="warning">{errorMessage}</Alert>}
      {loading && <CenteredLoading />}
      {previewLabelSrc !== '' && !loading && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6">Sticker Options</Typography>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="flex-start"
            spacing={2}>
            <Stack
              sx={{
                border: '1px solid #302B54',
                borderRadius: 4,
                padding: 2
              }}>
              <FormControlLabel
                label="Shop Name"
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={showLine1}
                    onChange={(event) => setShowLine1(event.target.checked)}
                  />
                }
              />
              <FormControlLabel
                label="Shop Phone"
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={showLine2}
                    onChange={(event) => setShowLine2(event.target.checked)}
                  />
                }
              />
              <FormControlLabel
                label="Oil Service"
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={showLine3}
                    onChange={(event) => setShowLine3(event.target.checked)}
                  />
                }
              />
              <FormControlLabel
                label="Inspection"
                labelPlacement="end"
                control={
                  <Checkbox
                    checked={showLine4}
                    onChange={(event) => setShowLine4(event.target.checked)}
                  />
                }
              />
            </Stack>
            <Box
              sx={{
                border: '1px solid #302B54',
                borderRadius: 4,
                padding: 2
              }}>
              <FormControl>
                <FormLabel id="print-sticker-logo-options">Size</FormLabel>
                <RadioGroup
                  aria-labelledby="print-sticker-logo-size-options"
                  name="print-sticker-logo-size"
                  value={labelVersion.size}
                  onChange={(event) =>
                    setLabelVersion({
                      size: event.target.value,
                      version: 'none'
                    })
                  }>
                  <FormControlLabel
                    value="1x3"
                    control={<Radio />}
                    label="1x3"
                  />
                  <FormControlLabel
                    value="2x4"
                    control={<Radio />}
                    label="2x4"
                  />
                  <FormControlLabel
                    value="2x2.3125"
                    control={<Radio />}
                    label="2x2.3125"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box
              sx={{
                border: '1px solid #302B54',
                borderRadius: 4,
                padding: 2
              }}>
              <FormControl>
                <FormLabel id="print-sticker-logo-options">Image</FormLabel>
                <RadioGroup
                  aria-labelledby="print-sticker-logo-options"
                  name="print-sticker-logo"
                  value={labelVersion.version}
                  onChange={(event) =>
                    setLabelVersion((prev) => ({
                      ...prev,
                      version: event.target.value
                    }))
                  }>
                  {labelVersion.size === '1x3' && (
                    <>
                      <FormControlLabel
                        value="none"
                        control={<Radio />}
                        label="None"
                      />
                      {shopLogoDymoData && (
                        <>
                          <FormControlLabel
                            value="smallAddress"
                            control={<Radio />}
                            label="Small"
                          />
                          <FormControlLabel
                            value="largeAddress"
                            control={<Radio />}
                            label="Large"
                          />
                        </>
                      )}
                    </>
                  )}
                  {labelVersion.size === '2x4' && (
                    <>
                      <FormControlLabel
                        value="none"
                        control={<Radio />}
                        label="None"
                      />
                      {shopLogoDymoData && (
                        <>
                          <FormControlLabel
                            value="middleShipping"
                            control={<Radio />}
                            label="Middle"
                          />
                          <FormControlLabel
                            value="rightShipping"
                            control={<Radio />}
                            label="Right"
                          />
                        </>
                      )}
                    </>
                  )}
                  {labelVersion.size === '2x2.3125' && (
                    <>
                      <FormControlLabel
                        value="none"
                        control={<Radio />}
                        label="None"
                      />
                      {shopLogoDymoData && (
                        <>
                          <FormControlLabel
                            value="middleNormal"
                            control={<Radio />}
                            label="Middle"
                          />
                        </>
                      )}
                    </>
                  )}
                </RadioGroup>
              </FormControl>
            </Box>
            <Stack
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
              spacing={1}>
              <img
                id="previewLabelImage"
                src={previewLabelSrc}
                alt="label preview"
              />
              {printedLabelSrc === '' ? (
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={() => printLabel()}
                  sx={{ marginLeft: 30 }}>
                  Print
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  sx={{ marginLeft: 30 }}>
                  <Box
                    sx={{
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'inline-flex',
                      p: 0.5,
                      mr: 1
                    }}>
                    <CheckIcon sx={{ color: 'white', fontSize: '90%' }} />
                  </Box>
                  Sent to Printer!
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default PreviewAndPrint
